
import React, { useMemo, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './CKEditorField.css';

const CKEditorField = ({
  value = '',
  onChange,
  placeholder = 'Write your email...',
  readOnly = false,
  className = '',
}) => {
  const quillRef = useRef(null);

  const handleChange = useCallback(
    (content, delta, source, editor) => {
      if (onChange) {
        onChange(content, delta, source, editor);
      }
    },
    [onChange]
  );

  const handleUndo = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.history.undo();
      editor.focus();
    }
  }, []);

  const handleRedo = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.history.redo();
      editor.focus();
    }
  }, []);

  const insertLink = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    const selectedText = range
      ? editor.getText(range.index, range.length)
      : '';

    const url = window.prompt('Enter the URL:', 'https://');

    if (!url || !url.trim()) return;

    let normalizedUrl = url.trim();

    // Only allow safe web links and email links.
    if (!/^(https?:\/\/|mailto:)/i.test(normalizedUrl)) {
      normalizedUrl = `https://${normalizedUrl}`;
    }

    if (!/^https?:\/\/[^\s]+$|^mailto:[^\s]+$/i.test(normalizedUrl)) {
      window.alert('Please enter a valid HTTP, HTTPS, or email link.');
      return;
    }

    if (range && range.length > 0) {
      editor.formatText(range.index, range.length, 'link', normalizedUrl);
    } else {
      const text = normalizedUrl;
      editor.insertText(range.index, text, 'user');
      editor.formatText(range.index, text.length, 'link', normalizedUrl);
      editor.setSelection(range.index + text.length, 0, 'silent');
    }

    editor.focus();
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          // [{ font: [] }],
          // [{ size: ['small', false, 'large', 'huge'] }],
          ['bold', 'italic', 'underline', 'strike'],
          // [{ color: [] }, { background: [] }],
          // [{ script: 'sub' }, { script: 'super' }],
          // [{ header: [1, 2, 3, false] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
          // ['link', 'blockquote', 'code-block'],
          // ['clean'],
        ],
        handlers: {
          link: insertLink,
        },
      },
      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true,
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    [insertLink]
  );

  const formats = useMemo(
    () => [
      'font',
      'size',
      'bold',
      'italic',
      'underline',
      'strike',
      'color',
      'background',
      'script',
      'header',
      'list',
      'indent',
      'align',
      'link',
      'blockquote',
      'code-block',
    ],
    []
  );

  return (
    <div className={`email-editor ${className}`}>
      <div className="email-editor-header">
        <div className="email-editor-heading">
          <span className="email-editor-title">Email message</span>
          <span className="email-editor-subtitle">
            Format your message before sending
          </span>
        </div>

        <div className="email-editor-actions">
          <button
            type="button"
            className="email-editor-action"
            onClick={handleUndo}
            disabled={readOnly}
            title="Undo"
            aria-label="Undo"
          >
            ↶
          </button>

          <button
            type="button"
            className="email-editor-action"
            onClick={handleRedo}
            disabled={readOnly}
            title="Redo"
            aria-label="Redo"
          >
            ↷
          </button>

          <button
            type="button"
            className="email-editor-action email-editor-clear"
            onClick={() => {
              const editor = quillRef.current?.getEditor();
              if (!editor || readOnly) return;

              const range = editor.getSelection(true);
              if (range && range.length > 0) {
                editor.removeFormat(range.index, range.length, 'user');
              } else {
                editor.removeFormat(
                  Math.max(0, (editor.getLength() || 1) - 1),
                  0,
                  'user'
                );
              }

              editor.focus();
            }}
            disabled={readOnly}
            title="Remove formatting from selected text"
          >
            Clear formatting
          </button>
        </div>
      </div>

      <div className="email-editor-paper">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
};

export default CKEditorField;